import { useState } from 'react';
import api from '../../hooks/useApi';

// ── Answering "Do you work from home on the same day every week?" ────────────
//
// The Action asking somebody with a reserved desk for their usual home days
// (src/lib/homeDaysActions.js) is answered here, on the card, rather than by
// sending them off to find the setting. Ticking days and saving them, or saying
// there is no regular day, are both answers, and either closes the Action: the
// save goes to the same endpoint the Bookings page menu uses, which re-syncs it.
//
// The person it asks answers for themselves. Action Oversight can answer for
// them (forName), when they have said their days some other way: that saves
// through the Action, which records who answered on the thread.

const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri']];

export default function HomeDaysAnswer({ actionId, canAnswer, forName, onAnswered }) {
  const [ticked, setTicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async (days) => {
    setBusy(true); setError('');
    try {
      const answer = { home_days: days.length ? days.join(',') : null };
      await (forName ? api.put(`actions/${actionId}/home-days`, answer) : api.put('whereabouts/usual', answer));
      onAnswered && onAnswered();
    } catch (e) {
      setError(e?.message || 'That could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!canAnswer) return null;
  const toggle = (k) => setTicked(t => DAYS.map(([d]) => d).filter(d => (d === k) !== t.includes(d)));

  return (
    <div className="mt-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5" onClick={e => e.stopPropagation()}>
      {forName && <div className="text-[8px] text-gray-500 mb-1">Answering for {forName}</div>}
      <div className="flex flex-wrap items-center gap-1">
        {DAYS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => toggle(k)} disabled={busy} aria-pressed={ticked.includes(k)}
            className={`px-2 py-1 text-[9px] font-semibold rounded border disabled:opacity-40 ${ticked.includes(k)
              ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
        <button type="button" onClick={() => save(ticked)} disabled={busy || !ticked.length}
          className="ml-1 px-2.5 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:opacity-90 disabled:opacity-30">
          {forName ? 'Save their home days' : 'Save my home days'}
        </button>
        <button type="button" onClick={() => save([])} disabled={busy || ticked.length > 0}
          title={ticked.length ? 'Untick the days first' : undefined}
          className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-white disabled:opacity-30">
          {forName ? "They don't have a regular home day" : "I don't have a regular home day"}
        </button>
      </div>
      {error && <div className="text-[9px] text-red-700 mt-1">{error}</div>}
    </div>
  );
}
